import { useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useNavigate } from 'react-router-dom';
import type { ClipPublic, ClipStatus } from '@proref/types';
import { useClipsQuery } from '../../hooks/use-clips';
import { PageHeader } from '../../components/shared/PageHeader';
import { Button } from '../../components/shared/Button';
import { Checkbox } from '../../components/shared/Checkbox';
import { Input } from '../../components/shared/Input';
import { Spinner } from '../../components/shared/Spinner';
import { ListSkeleton } from '../../components/shared/Skeleton';
import { Icon } from '../../components/shared/Icons';
import { apiErrorMessage } from '../../lib/api';
import { notifyWarning } from '../../lib/toast';
import { useInfiniteScroll } from '../../hooks/use-infinite-scroll';
import { UploadClipDialog } from './UploadClipDialog';
import { BulkUploadClipsDialog } from './BulkUploadClipsDialog';
import { DeleteClipDialog } from './DeleteClipDialog';
import { UnpublishClipDialog } from './UnpublishClipDialog';
import { BulkDeleteClipsDialog } from './BulkDeleteClipsDialog';
import { BulkUnpublishClipsDialog } from './BulkUnpublishClipsDialog';

// Derived row state — combines `status` with `published_at` so a single chip carries the
// whole lifecycle the admin cares about (uploading → processing → draft → published, or
// failed). "Draft" makes the transcoded-but-unpublished case visible at a glance —
// without it "Ready" was ambiguous (does it mean playable, or live to referees?).
export type RowState = 'uploading' | 'processing' | 'failed' | 'draft' | 'published';

export function deriveRowState(clip: { status: ClipStatus; published_at: string | null }): RowState {
  if (clip.published_at) return 'published';
  if (clip.status === 'ready') return 'draft';
  return clip.status;
}

// A function (not a static map) because the label is translated — every call site now needs
// a `t` from its own `useTranslation()`, same pattern as the plain-helper functions in
// lib/clip-file.ts.
export function statusLabel(t: TFunction, state: RowState): string {
  return t(`admin.clips.statusLabel.${state}`);
}

export const STATUS_CHIP: Record<RowState, string> = {
  uploading: 'bg-accent-tint text-accent',
  processing: 'bg-accent-tint text-accent',
  failed: 'bg-danger-tint text-danger',
  draft: 'bg-warn-tint text-warn',
  published: 'bg-success-tint text-success',
};

// In-flight states block navigation to the editor — the admin can't meaningfully tag
// (or play) a clip that hasn't finished transcoding, so we intercept the row click
// with a warning toast instead.
const ROW_NAVIGABLE: Record<RowState, boolean> = {
  uploading: false,
  processing: false,
  failed: false, // failed clips can't be tagged either; future: re-upload action
  draft: true,
  published: true,
};

// ─── Filters ───────────────────────────────────────────────────────────────────
//
// All client-side: we already fetch the full list for the in-flight polling, so deriving
// filtered views is free + instant. If a federation ever hits thousands of clips we can
// move some of these to server-side params without changing the UI shape.
//
// Rule + Uploader dropdowns are populated from the unfiltered list itself — no second
// endpoint, options auto-trim to what's actually present.

type TimeRange = 'all' | '7d' | '30d' | '90d';

interface ClipFilters {
  search: string;
  // 'all' is the no-filter sentinel for the dropdowns; the other values are typed unions.
  status: RowState | 'all';
  ruleId: string | 'all';
  // Managed-tag filter — narrows to clips carrying this vocabulary tag. Client-side (each clip
  // payload already embeds its tags), so it composes with the other axes for free.
  tagId: string | 'all';
  uploadedById: string | 'all';
  timeRange: TimeRange;
}

const INITIAL_FILTERS: ClipFilters = {
  search: '',
  status: 'all',
  ruleId: 'all',
  tagId: 'all',
  uploadedById: 'all',
  timeRange: 'all',
};

// How many rows the table reveals per infinite-scroll step. The list query already pulls the
// full federation library (needed for in-flight polling + bulk select-all), so this is pure
// client-side windowing — we render a growing slice instead of every row at once.
const CLIPS_WINDOW = 20;

function hasActiveFilters(f: ClipFilters): boolean {
  return (
    f.search.trim() !== '' ||
    f.status !== 'all' ||
    f.ruleId !== 'all' ||
    f.tagId !== 'all' ||
    f.uploadedById !== 'all' ||
    f.timeRange !== 'all'
  );
}

// Pure — no React, no Date.now mocking concerns; the caller passes `now` so tests can pin
// the time range against a fixed epoch instead of wall-clock drift. Time range measures
// against uploaded_at (when the clip first appeared) per the product call, not last_edited_at.
function applyFilters(clips: ClipPublic[], f: ClipFilters, now: number = Date.now()): ClipPublic[] {
  const cutoffDaysByRange: Record<TimeRange, number | null> = {
    all: null,
    '7d': 7,
    '30d': 30,
    '90d': 90,
  };
  const cutoffDays = cutoffDaysByRange[f.timeRange];
  const cutoffMs = cutoffDays !== null ? cutoffDays * 86_400_000 : null;
  const search = f.search.trim().toLowerCase();

  return clips.filter((c) => {
    if (search && !c.title.toLowerCase().includes(search)) return false;
    if (f.status !== 'all' && deriveRowState(c) !== f.status) return false;
    if (f.ruleId !== 'all' && c.rule_article_id !== f.ruleId) return false;
    // Tags are an optional embed (undefined on clips fetched before tagging existed) — coalesce
    // to an empty list so an untagged clip simply fails the filter rather than throwing.
    if (f.tagId !== 'all' && !(c.tags ?? []).some((t) => t.id === f.tagId)) return false;
    if (f.uploadedById !== 'all' && c.uploaded_by !== f.uploadedById) return false;
    if (cutoffMs !== null && now - new Date(c.uploaded_at).getTime() > cutoffMs) return false;
    return true;
  });
}

// Bulk-action dialog state lives at the page level so the modal mounts outside the table.
// Single-clip kebab dialogs use a different state slot ('activeRowDialog') so a per-row
// destructive action doesn't fight with a bulk action that happens to be open.
type RowDialog = { kind: 'delete' | 'unpublish'; clip: ClipPublic } | null;
type BulkDialog = 'delete' | 'unpublish' | null;

export function ClipsPage() {
  const { t } = useTranslation();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [activeRowDialog, setActiveRowDialog] = useState<RowDialog>(null);
  const [bulkDialog, setBulkDialog] = useState<BulkDialog>(null);
  const [filters, setFilters] = useState<ClipFilters>(INITIAL_FILTERS);
  // Selection state — set of clip IDs. Set chosen over an array because membership /
  // toggle ops dominate (every row checks if it's selected on every render). Survives
  // filter changes intentionally: an admin picks rows then filters down to view them and
  // expects their picks to stick (the count in the SelectionBar reflects total, not visible).
  const [selection, setSelection] = useState<Set<string>>(new Set());

  // Fetch once at the page level (cache key ['clips'] is shared, so BulkDialogs' own call
  // is a cache hit) and derive the filtered view here. Polling logic stays inside the hook.
  const clipsQuery = useClipsQuery();
  const allClips = useMemo(() => clipsQuery.data ?? [], [clipsQuery.data]);
  const filteredClips = useMemo(() => applyFilters(allClips, filters), [allClips, filters]);

  // Infinite-scroll window over the filtered list. Reset back to the first page whenever the
  // filters change (a new object identity on every setFilters) so a narrowed search doesn't
  // inherit a large window scrolled open under the previous filter.
  const [visibleCount, setVisibleCount] = useState(CLIPS_WINDOW);
  useEffect(() => {
    setVisibleCount(CLIPS_WINDOW);
  }, [filters]);
  const visibleClips = useMemo(() => filteredClips.slice(0, visibleCount), [filteredClips, visibleCount]);
  const hasMore = visibleCount < filteredClips.length;

  function toggleSelection(id: string) {
    setSelection((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function clearSelection() {
    setSelection(new Set());
  }
  function selectAll(ids: string[]) {
    setSelection(new Set(ids));
  }
  // After a bulk action: drop the succeeded IDs from selection, keep the failed ones so
  // the user can retry without re-checking everything. Failed clips remain in the list.
  function pruneSelection(succeededIds: string[]) {
    setSelection((prev) => {
      const next = new Set(prev);
      succeededIds.forEach((id) => next.delete(id));
      return next;
    });
  }

  return (
    <>
      <PageHeader
        title={t('admin.clips.list.title')}
        sub={t('admin.clips.list.sub')}
        actions={
          <div className="flex items-center gap-2">
            {/* Bulk gets the quieter treatment: one clip at a time is still the common case, and
                it's the flow that hands you straight to the editor afterwards. */}
            <Button variant="default" onClick={() => setBulkOpen(true)}>
              <Icon.upload className="w-3.5 h-3.5" />
              {t('admin.clips.list.uploadMany')}
            </Button>
            <Button variant="accent" onClick={() => setUploadOpen(true)}>
              <Icon.upload className="w-3.5 h-3.5" />
              {t('admin.clips.list.uploadOne')}
            </Button>
          </div>
        }
      />

      <ClipsFilterBar
        filters={filters}
        onChange={setFilters}
        allClips={allClips}
      />

      <ClipsList
        clips={visibleClips}
        allClipsCount={allClips.length}
        isLoading={clipsQuery.isLoading}
        isError={clipsQuery.isError}
        error={clipsQuery.error}
        filtersActive={hasActiveFilters(filters)}
        onClearFilters={() => setFilters(INITIAL_FILTERS)}
        selection={selection}
        onToggle={toggleSelection}
        onSelectAll={selectAll}
        onClear={clearSelection}
        onOpenBulk={(kind) => setBulkDialog(kind)}
        onRowAction={(kind, clip) => setActiveRowDialog({ kind, clip })}
        hasMore={hasMore}
        onLoadMore={() => setVisibleCount((c) => c + CLIPS_WINDOW)}
      />

      <UploadClipDialog open={uploadOpen} onClose={() => setUploadOpen(false)} />
      <BulkUploadClipsDialog open={bulkOpen} onClose={() => setBulkOpen(false)} />

      {/* Single-row destructive dialogs — driven by the kebab menu. */}
      {activeRowDialog?.kind === 'delete' && (
        <DeleteClipDialog
          open
          onClose={() => setActiveRowDialog(null)}
          clipId={activeRowDialog.clip.id}
          clipTitle={activeRowDialog.clip.title}
        />
      )}
      {activeRowDialog?.kind === 'unpublish' && (
        <UnpublishClipDialog
          open
          onClose={() => setActiveRowDialog(null)}
          clipId={activeRowDialog.clip.id}
          clipTitle={activeRowDialog.clip.title}
        />
      )}

      {/* Bulk dialogs — open from the selection bar above the table. Snapshots the current
          selection into the dialog so live changes to selection state don't surprise the
          confirmed batch. */}
      <BulkDialogs
        kind={bulkDialog}
        selection={selection}
        onClose={() => setBulkDialog(null)}
        onDone={(result) => pruneSelection(result.succeededIds)}
      />
    </>
  );
}

interface BulkDialogsProps {
  kind: BulkDialog;
  selection: Set<string>;
  onClose: () => void;
  onDone: (result: { succeededIds: string[]; failedIds: string[] }) => void;
}

// Lifts the dialog logic into one wrapper so the page body stays readable. Pulls fresh
// clip data from the list query to resolve titles + the "currently published" filter (the
// dialogs need clip metadata, not just IDs — for the title list inside the modal AND for
// unpublish, which only fires on published clips even if the selection was mixed).
function BulkDialogs({ kind, selection, onClose, onDone }: BulkDialogsProps) {
  const { data } = useClipsQuery();
  const clips = data ?? [];
  const selected = useMemo(() => clips.filter((c) => selection.has(c.id)), [clips, selection]);
  const livePartOfSelection = useMemo(() => selected.filter((c) => c.published_at), [selected]);

  if (kind === 'delete') {
    return (
      <BulkDeleteClipsDialog
        open
        onClose={onClose}
        clips={selected.map((c) => ({ id: c.id, title: c.title }))}
        onDone={onDone}
      />
    );
  }
  if (kind === 'unpublish') {
    return (
      <BulkUnpublishClipsDialog
        open
        onClose={onClose}
        clips={livePartOfSelection.map((c) => ({ id: c.id, title: c.title }))}
        onDone={onDone}
      />
    );
  }
  return null;
}

// ─── Filter bar ────────────────────────────────────────────────────────────────

interface ClipsFilterBarProps {
  filters: ClipFilters;
  onChange: (next: ClipFilters) => void;
  // Used to derive the rule + uploader dropdown options. We pull from the UNFILTERED list
  // so the dropdowns don't shrink as the user filters (would hide options the admin might
  // want to switch to next), and we de-duplicate to one entry per id.
  allClips: ClipPublic[];
}

// Inline search + four native selects + Clear. Native <select> over a custom dropdown:
// browser-native a11y/keyboard/typeahead are free, and the visual footprint is small
// enough that styling them to match Input gets us 95% of the way to a custom widget.
function ClipsFilterBar({ filters, onChange, allClips }: ClipsFilterBarProps) {
  const { t } = useTranslation();
  const ruleOptions = useMemo(() => deriveRuleOptions(allClips), [allClips]);
  const tagOptions = useMemo(() => deriveTagOptions(allClips), [allClips]);
  const uploaderOptions = useMemo(() => deriveUploaderOptions(allClips, t), [allClips, t]);
  const active = hasActiveFilters(filters);

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2 rounded border border-line bg-surface p-2 shadow-1">
      <div className="relative min-w-[220px] flex-1">
        <Icon.search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
        <Input
          type="search"
          placeholder={t('admin.clips.list.searchPlaceholder')}
          value={filters.search}
          onChange={(e) => onChange({ ...filters, search: e.target.value })}
          className="pl-9"
          aria-label={t('admin.clips.list.searchAria')}
        />
      </div>

      <FilterSelect
        label={t('admin.clips.list.filterStatus')}
        value={filters.status}
        onChange={(v) => onChange({ ...filters, status: v as ClipFilters['status'] })}
        options={[
          { value: 'all', label: t('admin.clips.list.filterAllStatuses') },
          { value: 'draft', label: statusLabel(t, 'draft') },
          { value: 'published', label: statusLabel(t, 'published') },
          { value: 'uploading', label: statusLabel(t, 'uploading') },
          { value: 'processing', label: statusLabel(t, 'processing') },
          { value: 'failed', label: statusLabel(t, 'failed') },
        ]}
      />

      <FilterSelect
        label={t('admin.clips.list.filterRule')}
        value={filters.ruleId}
        onChange={(v) => onChange({ ...filters, ruleId: v })}
        options={[{ value: 'all', label: t('admin.clips.list.filterAllRules') }, ...ruleOptions]}
      />

      {/* Tag filter only appears once the federation's clips actually carry tags — an untagged
          library shows no empty control (the vocabulary is derived from the clips themselves). */}
      {tagOptions.length > 0 && (
        <FilterSelect
          label={t('admin.clips.list.filterTag')}
          value={filters.tagId}
          onChange={(v) => onChange({ ...filters, tagId: v })}
          options={[{ value: 'all', label: t('admin.clips.list.filterAllTags') }, ...tagOptions]}
        />
      )}

      <FilterSelect
        label={t('admin.clips.list.filterUploadedBy')}
        value={filters.uploadedById}
        onChange={(v) => onChange({ ...filters, uploadedById: v })}
        options={[{ value: 'all', label: t('admin.clips.list.filterAnyone') }, ...uploaderOptions]}
      />

      <FilterSelect
        label={t('admin.clips.list.filterTimeRange')}
        value={filters.timeRange}
        onChange={(v) => onChange({ ...filters, timeRange: v as TimeRange })}
        options={[
          { value: 'all', label: t('admin.clips.list.filterAnyTime') },
          { value: '7d', label: t('admin.clips.list.filterLast7') },
          { value: '30d', label: t('admin.clips.list.filterLast30') },
          { value: '90d', label: t('admin.clips.list.filterLast90') },
        ]}
      />

      {active && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onChange(INITIAL_FILTERS)}
          aria-label={t('admin.clips.list.clearAria')}
        >
          <Icon.close className="h-3.5 w-3.5" />
          {t('admin.clips.list.clearLabel')}
        </Button>
      )}
    </div>
  );
}

// Token-matched native select. Same height + border + focus ring as Input so the bar reads
// as one unit. Native widget keeps keyboard / type-ahead / a11y free.
function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <select
      aria-label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={[
        'h-10 rounded-sm border border-line-2 bg-surface px-2.5 pr-7 text-sm text-ink',
        'outline-none transition-[border-color,box-shadow]',
        'focus:border-ink-3 focus:shadow-[0_0_0_3px_color-mix(in_oklch,var(--ink)_10%,transparent)]',
      ].join(' ')}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

// Pull the unique rules referenced by the current clip list. Each entry is
// `{ value: rule_article_id, label: "8:5 · Fouls" }`. Sorted by article_number lexicographically
// — fine for printed labels like "8:5"/"7:11" since they're not strictly numeric and the
// rule-book ordering itself follows insertion order at the API.
function deriveRuleOptions(clips: ClipPublic[]): { value: string; label: string }[] {
  const seen = new Map<string, string>();
  for (const c of clips) {
    if (c.rule_article && !seen.has(c.rule_article.id)) {
      seen.set(c.rule_article.id, `${c.rule_article.article_number} · ${c.rule_article.title}`);
    }
  }
  return Array.from(seen.entries())
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

// Pull the unique managed tags carried by the current clip list — one entry per tag id. Derived
// from the clips' own embedded tags (not a separate vocabulary fetch) so it works identically for
// a federation_admin (own tenant) and the super_admin console (cross-tenant, per sport) without
// the per-federation scoping the /clip-tags endpoint would need.
function deriveTagOptions(clips: ClipPublic[]): { value: string; label: string }[] {
  const seen = new Map<string, string>();
  for (const c of clips) {
    for (const t of c.tags ?? []) {
      if (!seen.has(t.id)) seen.set(t.id, t.label);
    }
  }
  return Array.from(seen.entries())
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

// Pull the unique uploaders, falling back to "Unknown" when the user row is gone (uploaded_by
// is ON DELETE SET NULL — the clip survives, the name doesn't).
function deriveUploaderOptions(clips: ClipPublic[], t: TFunction): { value: string; label: string }[] {
  const seen = new Map<string, string>();
  for (const c of clips) {
    if (c.uploaded_by && !seen.has(c.uploaded_by)) {
      seen.set(c.uploaded_by, c.uploaded_by_user?.full_name ?? t('admin.clips.list.unknownUploader'));
    }
  }
  return Array.from(seen.entries())
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

// ─── Clips list ────────────────────────────────────────────────────────────────

interface ClipsListProps {
  clips: ClipPublic[];
  allClipsCount: number;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  filtersActive: boolean;
  onClearFilters: () => void;
  selection: Set<string>;
  onToggle: (id: string) => void;
  onSelectAll: (ids: string[]) => void;
  onClear: () => void;
  onOpenBulk: (kind: 'delete' | 'unpublish') => void;
  onRowAction: (kind: 'delete' | 'unpublish', clip: ClipPublic) => void;
  // Infinite-scroll paging over the client-side window: whether more rows remain, and the
  // callback to reveal the next slice as the sentinel nears view.
  hasMore: boolean;
  onLoadMore: () => void;
}

function ClipsList({
  clips,
  allClipsCount,
  isLoading,
  isError,
  error,
  filtersActive,
  onClearFilters,
  selection,
  onToggle,
  onSelectAll,
  onClear,
  onOpenBulk,
  onRowAction,
  hasMore,
  onLoadMore,
}: ClipsListProps) {
  // Reveal the next window as the sentinel nears the viewport; disarmed once every filtered row
  // is on screen so we never fire past the end.
  const { t } = useTranslation();
  const sentinelRef = useInfiniteScroll(onLoadMore, hasMore);
  // First load only (no clips fetched yet); a background refetch shows nothing here.
  if (isLoading) {
    return <ListSkeleton rows={6} />;
  }

  // Only blank to the error state when there's genuinely nothing cached (allClipsCount === 0) —
  // a failed background refetch keeps the existing rows on screen instead of wiping them.
  if (isError && allClipsCount === 0) {
    return (
      <div className="rounded border border-danger/40 bg-danger/10 px-4 py-3 text-[13px] text-danger">
        {apiErrorMessage(error, t('admin.clips.list.couldNotLoad'))}
      </div>
    );
  }

  // Two empty states: "no clips at all" (federation has zero) vs "no clips match the
  // current filters" — different copy + the second offers a one-click escape back to all.
  if (allClipsCount === 0) {
    return (
      <div className="grid place-items-center rounded border border-dashed border-line-2 bg-bg-2 px-6 py-12 text-center">
        <p className="max-w-md text-[13px] text-muted">
          <Trans i18nKey="admin.clips.list.emptyNoClips" components={{ bold: <span className="font-semibold text-ink" /> }} />
        </p>
      </div>
    );
  }

  if (clips.length === 0 && filtersActive) {
    return (
      <div className="grid place-items-center rounded border border-dashed border-line-2 bg-bg-2 px-6 py-12 text-center">
        <p className="max-w-md text-[13px] text-muted">{t('admin.clips.list.emptyNoMatches')}</p>
        <Button variant="ghost" size="sm" className="mt-3" onClick={onClearFilters}>
          {t('admin.clips.list.clearFilters')}
        </Button>
      </div>
    );
  }

  // Selection metrics — recomputed every render but cheap (linear scan over the visible page).
  const selectedCount = clips.filter((c) => selection.has(c.id)).length;
  const livePublishedSelected = clips.filter((c) => selection.has(c.id) && c.published_at).length;
  // Delete needs the selection to be entirely unpublished — same single-clip rule (the
  // API would 409 the published one, but pre-gating here makes the constraint visible).
  const hasPublishedInSelection = livePublishedSelected > 0;
  const allChecked = selectedCount > 0 && selectedCount === clips.length;
  const someChecked = selectedCount > 0 && selectedCount < clips.length;

  return (
    <>
      {selectedCount > 0 && (
        <SelectionBar
          selectedCount={selectedCount}
          livePublishedCount={livePublishedSelected}
          canDelete={!hasPublishedInSelection}
          onUnpublish={() => onOpenBulk('unpublish')}
          onDelete={() => onOpenBulk('delete')}
          onClear={onClear}
        />
      )}

      {/* overflow-x-auto (not -hidden): the row carries title, status, two attributions and a date —
          they can't compress to a phone's width, so the container scrolls instead. */}
      <div className="overflow-x-auto rounded border border-line bg-surface">
        <table className="w-full min-w-[760px] text-left text-[13px]">
          <thead>
            <tr className="border-b border-line text-[11px] uppercase tracking-[0.05em] text-muted">
              <th className="px-4 py-2.5 w-[40px]">
                <Checkbox
                  checked={allChecked}
                  indeterminate={someChecked}
                  onChange={() => {
                    if (selectedCount === clips.length) onClear();
                    else onSelectAll(clips.map((c) => c.id));
                  }}
                  aria-label={allChecked ? t('admin.clips.list.clearSelectionAria') : t('admin.clips.list.selectAllAria')}
                />
              </th>
              <th className="px-4 py-2.5 font-semibold">{t('admin.clips.list.colTitle')}</th>
              <th className="px-4 py-2.5 font-semibold">{t('admin.clips.list.colStatus')}</th>
              <th className="px-4 py-2.5 font-semibold">{t('admin.clips.list.colUploadedBy')}</th>
              <th className="px-4 py-2.5 font-semibold">{t('admin.clips.list.colLastEditedBy')}</th>
              <th className="px-4 py-2.5 font-semibold">{t('admin.clips.list.colLastEdited')}</th>
              {/* Empty header for the kebab column — content-only, no label. */}
              <th className="px-4 py-2.5 font-semibold w-[44px]" aria-label={t('admin.clips.list.colActionsAria')} />
            </tr>
          </thead>
          <tbody>
            {clips.map((c) => (
              <ClipRow
                key={c.id}
                clip={c}
                isSelected={selection.has(c.id)}
                onToggleSelect={() => onToggle(c.id)}
                onAction={onRowAction}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Infinite-scroll sentinel — sits below the table (outside its overflow-hidden wrapper) so
          the IntersectionObserver can see it. Renders only while more rows remain. */}
      {hasMore && (
        <div ref={sentinelRef} className="grid place-items-center py-3 text-[11px] text-muted">
          <Spinner className="w-3.5 h-3.5" />
          <span className="mt-1">{t('admin.clips.list.loadingMore')}</span>
        </div>
      )}
    </>
  );
}

interface SelectionBarProps {
  selectedCount: number;
  livePublishedCount: number;
  canDelete: boolean;
  onUnpublish: () => void;
  onDelete: () => void;
  onClear: () => void;
}

// Floats above the table when at least one row is selected. Mirrors Linear/Notion's
// "selection toolbar" idiom — counts on the left, actions on the right, clear at the far
// right so it's the last thing the eye lands on if the user wants to bail.
function SelectionBar({
  selectedCount,
  livePublishedCount,
  canDelete,
  onUnpublish,
  onDelete,
  onClear,
}: SelectionBarProps) {
  const { t } = useTranslation();
  // Tooltip wording matches the single-clip rule — same constraint, just over a batch.
  const deleteBlockedReason = !canDelete ? t('admin.clips.list.deleteBlockedTooltip') : undefined;
  return (
    <div className="mb-3 flex items-center justify-between gap-3 rounded border border-line bg-surface px-4 py-2 shadow-1">
      <div className="text-[12.5px] text-ink-2">
        <span className="font-semibold text-ink">{selectedCount}</span>{' '}
        {t('admin.clips.list.selectedSuffix', { count: selectedCount })}
        {livePublishedCount > 0 && (
          <span className="ml-2 text-muted">
            · {livePublishedCount} {t('admin.clips.list.liveSuffix', { count: livePublishedCount })}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          onClick={onUnpublish}
          disabled={livePublishedCount === 0}
          title={livePublishedCount === 0 ? t('admin.clips.list.unpublishSelectedNoneTitle') : undefined}
        >
          <Icon.eyeOff className="w-3.5 h-3.5" />
          {t('admin.clips.list.unpublishButton', { count: livePublishedCount })}
        </Button>
        <Button
          variant="danger"
          size="sm"
          onClick={onDelete}
          disabled={!canDelete}
          title={deleteBlockedReason}
        >
          <Icon.trash className="w-3.5 h-3.5" />
          {t('admin.clips.list.deleteButton', { count: selectedCount })}
        </Button>
        <Button variant="ghost" size="sm" onClick={onClear}>
          {t('admin.clips.list.clearSelection')}
        </Button>
      </div>
    </div>
  );
}

interface ClipRowProps {
  clip: ClipPublic;
  isSelected: boolean;
  onToggleSelect: () => void;
  onAction: (kind: 'delete' | 'unpublish', clip: ClipPublic) => void;
}

function ClipRow({ clip, isSelected, onToggleSelect, onAction }: ClipRowProps) {
  const { t } = useTranslation();
  const state = deriveRowState(clip);
  const inFlight = state === 'uploading' || state === 'processing';
  const navigable = ROW_NAVIGABLE[state];
  const navigate = useNavigate();

  // In-flight clips: warn instead of navigating. The editor's video preview + decision
  // point picker depend on the transcoded HLS existing, so opening the editor before
  // Ready is a useless experience. Toast tells the admin to wait.
  function onRowClick() {
    if (navigable) {
      navigate(`/admin/clips/${clip.id}/edit`);
    } else {
      notifyWarning(t('admin.clips.list.transcodingToast'));
    }
  }

  // Falls back to em-dash when the user row is gone (uploaded_by is ON DELETE SET NULL,
  // so attribution survives but the embedded name is null). last_edited_by_user is the
  // same shape; both are optional on the type so the chain stays narrow.
  const uploaderName = clip.uploaded_by_user?.full_name ?? '—';
  const lastEditorName = clip.last_edited_by_user?.full_name ?? uploaderName;

  return (
    <tr
      className={
        'border-b border-line transition-colors last:border-0 ' +
        (isSelected ? 'bg-accent-tint/40 ' : '') +
        (navigable ? 'cursor-pointer hover:bg-surface-2' : 'cursor-not-allowed opacity-70')
      }
      onClick={onRowClick}
    >
      {/* stopPropagation so a checkbox click doesn't bubble to the row's navigate handler.
          The whole cell is the click target so the user doesn't need pixel-perfect aim. */}
      <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
        <Checkbox
          checked={isSelected}
          onChange={onToggleSelect}
          aria-label={t('admin.clips.list.selectRowAria', { title: clip.title })}
        />
      </td>
      <td className="px-4 py-2.5 text-ink">
        <div className="flex items-center gap-3">
          {/* Thumbnail. Whole row already navigates on click, so the poster inherits that
              — no extra handler needed. In-flight clips (no transcoded poster yet) get a
              neutral placeholder block so the row layout stays consistent across states. */}
          <div className="relative h-12 w-[80px] flex-none overflow-hidden rounded-sm border border-line-2 bg-bg-2">
            {clip.poster_url ? (
              <img src={clip.poster_url} alt="" className="h-full w-full object-cover" />
            ) : (
              <div className="grid h-full w-full place-items-center text-[9px] uppercase tracking-wider text-muted">
                {inFlight ? '…' : t('common.noPoster')}
              </div>
            )}
          </div>
          <div className="min-w-0">
            <div className="truncate font-medium text-ink" title={clip.title}>{clip.title}</div>
            {/* Description sits under the title — line-clamped so a long blurb can't blow
                up the row height. Hidden entirely when null so untagged/skeleton clips
                don't show an empty line of muted text. */}
            {clip.description && (
              <div className="mt-0.5 line-clamp-1 text-[12px] text-muted">{clip.description}</div>
            )}
          </div>
        </div>
      </td>
      <td className="px-4 py-2.5">
        <span
          className={
            'inline-flex h-[22px] items-center gap-1.5 rounded-full px-2.5 text-[11px] font-medium ' +
            STATUS_CHIP[state]
          }
        >
          {inFlight && <Spinner className="w-2.5 h-2.5" />}
          {statusLabel(t, state)}
        </span>
      </td>
      <td className="px-4 py-2.5 text-ink-2">{uploaderName}</td>
      <td className="px-4 py-2.5 text-ink-2">{lastEditorName}</td>
      <td className="px-4 py-2.5 font-mono text-[12px] text-muted">
        {new Date(clip.last_edited_at).toLocaleString()}
      </td>
      {/* Wrapped in stopPropagation so opening the menu doesn't bubble to the row's
          navigate-on-click handler. */}
      <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
        <RowActionsMenu clip={clip} state={state} onAction={onAction} />
      </td>
    </tr>
  );
}

interface RowActionsMenuProps {
  clip: ClipPublic;
  state: RowState;
  onAction: (kind: 'delete' | 'unpublish', clip: ClipPublic) => void;
}

// Width of the rendered menu in px — kept as a constant because the open-handler needs to
// compute the menu's left edge (right-aligned to the button) before the menu mounts, so we
// can't measure it from the DOM. 176px = 11rem = the w-44 the class would have given us.
const MENU_WIDTH_PX = 176;

// Kebab dropdown — Edit / Unpublish / Delete. Items are state-aware:
//   • Edit       — visible only for draft/published (in-flight clips aren't editable yet).
//   • Unpublish  — visible only when the clip is currently published.
//   • Delete     — always present, but DISABLED when published or in-flight. The tooltip
//                  (title attr) explains why so the admin doesn't think it's broken.
//
// Rendered with `position: fixed` (not `absolute`) so it escapes the table wrapper's
// `overflow-hidden` — that wrapper has to clip to keep the rounded corners, which would
// otherwise hide the dropdown the moment it tried to paint outside the row.
function RowActionsMenu({ clip, state, onAction }: RowActionsMenuProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // Viewport-relative coordinates for the menu — captured from the button's bounding rect
  // at open time. Null when closed; recomputed every open so the menu lands in the right
  // place after a window resize.
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  function toggle() {
    if (!open && buttonRef.current) {
      const r = buttonRef.current.getBoundingClientRect();
      // Right-align the menu to the button; 4px below matches the popover gap our other
      // floating UI (Modal header → content separator) uses.
      setPos({ top: r.bottom + 4, left: r.right - MENU_WIDTH_PX });
    }
    setOpen((v) => !v);
  }

  // Close on outside click + Escape — standard popover hygiene. The button is checked
  // separately so clicking the button itself only fires the toggle (not also close-from-
  // outside-click). We also close on scroll: a fixed-positioned menu would otherwise stay
  // pinned to its open-time coordinates while the page moved underneath.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const t = e.target as Node;
      if (!menuRef.current?.contains(t) && !buttonRef.current?.contains(t)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    function onScroll() {
      setOpen(false);
    }
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    // capture: true so we hear scroll events from nested scrollers too, not just window.
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  const editable = state === 'draft' || state === 'published';
  const isPublished = state === 'published';
  const inFlight = state === 'uploading' || state === 'processing';
  // Delete is gated on the same rule the API enforces (CLIP_PUBLISHED). The reason
  // string drives the tooltip + the menu item's disabled appearance.
  const deleteBlockedReason = isPublished
    ? t('admin.clips.list.menuDeleteBlockedPublished')
    : inFlight
      ? t('admin.clips.list.menuDeleteBlockedInFlight')
      : null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={toggle}
        className="grid h-8 w-8 place-items-center rounded-sm text-ink-2 hover:bg-surface-2 hover:text-ink"
        aria-label={t('admin.clips.list.rowActionsAria')}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Icon.more className="w-4 h-4" />
      </button>

      {open && pos && (
        <div
          ref={menuRef}
          role="menu"
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: MENU_WIDTH_PX }}
          className="z-50 overflow-hidden rounded-sm border border-line bg-surface shadow-pop"
        >
          {editable && (
            <MenuItem
              onClick={() => {
                setOpen(false);
                navigate(`/admin/clips/${clip.id}/edit`);
              }}
            >
              {t('admin.clips.list.menuEdit')}
            </MenuItem>
          )}
          {isPublished && (
            <MenuItem
              onClick={() => {
                setOpen(false);
                onAction('unpublish', clip);
              }}
            >
              <Icon.eyeOff className="w-3.5 h-3.5" />
              {t('admin.clips.list.menuUnpublish')}
            </MenuItem>
          )}
          <MenuItem
            danger
            disabled={Boolean(deleteBlockedReason)}
            title={deleteBlockedReason ?? undefined}
            onClick={() => {
              setOpen(false);
              onAction('delete', clip);
            }}
          >
            <Icon.trash className="w-3.5 h-3.5" />
            {t('admin.clips.list.menuDelete')}
          </MenuItem>
        </div>
      )}
    </>
  );
}

interface MenuItemProps {
  children: React.ReactNode;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
  title?: string;
}

function MenuItem({ children, onClick, danger, disabled, title }: MenuItemProps) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      title={title}
      className={
        'flex w-full items-center gap-2 px-3 py-2 text-left text-[12.5px] disabled:cursor-not-allowed disabled:opacity-50 ' +
        (danger ? 'text-danger hover:bg-danger-tint' : 'text-ink-2 hover:bg-surface-2 hover:text-ink')
      }
    >
      {children}
    </button>
  );
}
